import axios from 'axios';
import { getAuthStorage, removeAuthStorage, setAuthStorage } from './storage';

if (!process.env.REACT_APP_API_URL) {
  throw new Error(
    'REACT_APP_API_URL não configurado — defina a variável de ambiente antes de iniciar o app.',
  );
}

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 120000, // 2 minutos (timeout padrão, endpoints específicos podem sobrescrever)
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
    // 'ngrok-skip-browser-warning': 'true',
  },
});

// Interceptor para detectar FormData e remover Content-Type padrão
api.interceptors.request.use(
  config => {
    // Se o body for FormData, remove o Content-Type para o browser definir automaticamente
    if (config.data instanceof FormData) {
      // eslint-disable-next-line no-param-reassign
      delete config.headers['Content-Type'];
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  response => {
    // Intercepta respostas de aprovação de solicitação de acesso
    const url = response.config.url || '';
    const method = (response.config.method || '').toLowerCase();

    // Verifica se é uma requisição de aprovação
    const isAprovacao =
      url.includes('/solicitacoes-acesso/') && url.includes('/aprovar/') && method === 'put';

    if (isAprovacao) {
      // Tenta múltiplas estruturas de resposta possíveis
      const token =
        response.data?.data?.token_acesso ||
        response.data?.token_acesso ||
        (response.data?.data &&
        typeof response.data.data === 'object' &&
        'token_acesso' in response.data.data
          ? response.data.data.token_acesso
          : null);

      const solicitacaoId =
        response.data?.data?.id ||
        response.data?.id ||
        (response.data?.data && typeof response.data.data === 'object' && 'id' in response.data.data
          ? response.data.data.id
          : null);

      if (token && solicitacaoId) {
        // Salva no localStorage
        try {
          const storedTokens = localStorage.getItem('solicitacao_tokens');
          const tokens: Record<number, string> = storedTokens ? JSON.parse(storedTokens) : {};
          tokens[solicitacaoId] = token;
          localStorage.setItem('solicitacao_tokens', JSON.stringify(tokens));

          // Dispara evento para atualizar componentes
          window.dispatchEvent(
            new CustomEvent('token-recebido', {
              detail: { solicitacao_id: solicitacaoId, token },
            }),
          );
        } catch {
          // Ignora erros ao salvar token
        }
      }
    }

    return response;
  },

  async error => {
    // Se não há resposta (erro de rede, timeout, etc.), rejeita diretamente
    if (!error.response) {
      return Promise.reject(error);
    }

    if (error.response.status === 401) {
      if (
        error.config.url.includes('login') === true ||
        error.config.url.includes('recover') === true
      ) {
        return Promise.reject(error);
      }
      const token = await refreshToken();

      // eslint-disable-next-line no-param-reassign
      error.config.headers.Authorization = `Bearer ${token}`;

      return api(error.config);
    }

    return Promise.reject(error);
  },
);

const refreshToken = async () => {
  try {
    const storageAuth = await getAuthStorage();
    if (storageAuth) {
      api.defaults.headers.common.Authorization = `Bearer ${storageAuth.refresh_token}`;
    }
    const res = await api.post('/api/v1/login/refresh-token/');
    await setAuthStorage(res.data.data);

    const token = res.data.data.access_token;
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
    return token;
  } catch (error: any) {
    if (
      error.response?.status === 404 &&
      error.response?.data.detail === 'User or Session inactive'
    ) {
      await removeAuthStorage();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
};
